import { STORAGE_KEYS } from "../Utils/constants";
import apiService from "./apiService";

export const loginUser = async (credentials) => {
  try {
    const response = await apiService.post("/api/authenticate", credentials);
    const { accessToken } = response.data;

    localStorage.setItem(STORAGE_KEYS.TOKEN, accessToken);

    return response.data;
  } catch (error) {
    console.error("Login failed", error, error.response?.status);
    // Return a structured error object instead of throwing
    if (error.response?.status === 401) {
      throw "Invalid username or password.";
    }
    throw error;
  }
};

/**
 * Checks if the provided username exists and is valid by making a backend API call.
 *
 * @param {string} username - The username to validate.
 * @returns {Promise<Object>} - The response data from the backend if the username is valid.
 * @throws {Error} - Throws an error message for invalid username or other errors.
 */
export const checkUsername = async (username) => {
  try {
    // Send a GET request with the username as a query parameter
    const response = await apiService.get("/api/check-username", {
      params: { identifier: username },
    });
    return response.data;
  } catch (error) {
    console.error("Username validation failed:", error);

    // Handle specific error scenarios (e.g., 401 for invalid username)
    if (error.response?.status === 401) {
      throw new Error("Invalid username");
    }

    // For other errors, throw a generic error
    throw new Error("An error occurred while validating the username");
  }
};

export const forgotPassword = async (identifier) => {
  try {
    const response = await apiService.post("/api/forgot-password", null, {
      params: { identifier },
    });
    return response.data;
  } catch (error) {
    console.error("Forgot Password Error:", error);
    throw (
      error.response?.data || {
        error: "Failed to send forgot password request",
      }
    );
  }
};

// Function to fetch user details
let userDetailsPromise = null;

export const getUserDetails = () => {
  // If a request is already in flight, reuse it instead of sending another
  if (userDetailsPromise) return userDetailsPromise;

  userDetailsPromise = (async () => {
    try {
      const response = await apiService.get("/api/admin/profile");
      return response.data.data;
    } catch (error) {
      console.error("Failed to fetch user details", error);
      throw error;
    } finally {
      userDetailsPromise = null; // allow fresh fetches after this one finishes
    }
  })();

  return userDetailsPromise;
};

export const logout = () => {
  localStorage.removeItem(STORAGE_KEYS.TOKEN);
  localStorage.removeItem(STORAGE_KEYS.USER_INFO);
  window.location.href = "/login";
};

export const getNotifications = async () => {
  try {
    const response = await apiService.get("/api/notifications");
    return response.data;
  } catch (error) {
    console.error("Failed to fetch notifications", error);
    throw error;
  }
};

export const markAsReadNotification = async (id) => {
  try {
    const response = await apiService.put(`/api/notifications/read/${id}`);
    return response.data;
  } catch (error) {
    console.error("Failed to mark notification as read:", error);
    throw error;
  }
};
/**
 * Create a new membership card
 * @param {Object} cardData - The membership card data to send to the backend
 * @returns {Promise} - Response from the API
 */
export const createMembershipCard = async (cardData) => {
  try {
    // Send the POST request to the backend
    const response = await apiService.post("/api/membership-cards", cardData);

    // Return the response data
    return response.data;
  } catch (error) {
    console.error("Error creating membership card:", error);

    // Re-throw other errors
    throw error;
  }
};

/**
 * Fetch membership cards with pagination, search, and filter
 * @param {number} page - The current page number (default is 0)
 * @param {number} size - The number of items per page (default is 10)
 * @param {string} search - The search term for filtering by company name
 * @param {string} status - The status filter (e.g., 'Active', 'Inactive', 'Expired')
 * @returns {Promise} - Membership cards data
 */
export const getMembershipCards = async (
  page = 0,
  size = 10,
  search = "",
  status = "",
) => {
  try {
    const response = await apiService.get("/api/membership-cards", {
      params: { page, size, search, status },
    });
    return response.data.data;
  } catch (error) {
    console.error("Error fetching membership cards:", error);
    throw error;
  }
};

export const getCustomerTransactions = async (
  membershipCardNumber,
  page = 0,
  size = 10,
) => {
  try {
    const response = await apiService.get(
      "/api/transactions/by-membership-card",
      {
        params: { membershipCardNumber, page, size },
      },
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching customer transactions:", error);
    return { transactions: [], totalItems: 0 };
  }
};

/**
 * Delete a membership card by ID
 * @param {number} id - The ID of the membership card to delete
 * @returns {Promise} - Response from the API
 */
export const deleteMembershipCard = async (id) => {
  try {
    const response = await apiService.delete(`/api/membership-cards/${id}`);
    return response.data;
  } catch (error) {
    console.error(`Error deleting membership card with ID ${id}:`, error);
    throw error;
  }
};

export const updateMembershipCard = async (id, cardData) => {
  try {
    const response = await apiService.put(
      `/api/membership-cards/${id}`,
      cardData,
    );
    return response.data;
  } catch (error) {
    console.error("Error updating membership card:", error);
    throw error;
  }
};

export const topupBalance = async (cardNumber, amount) => {
  try {
    const response = await apiService.post(
      `/api/membership-cards/top-up/${cardNumber}?amount=${amount}`,
    );
    return response.data;
  } catch (error) {
    const errorMessage =
      error.response?.data?.detail || "Failed to add money. Please try again.";
    throw new Error(errorMessage);
  }
};

// Fetch product based on the category id
export const getMembershipCardsCount = async () => {
  try {
    const response = await apiService.get(
      "/api/membership-cards/cardsCountByType",
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching cards count:", error);
    throw error;
  }
};

// Fetch settings from the backend
export const getSettings = async () => {
  try {
    const response = await apiService.get("/api/settings");
    return response.data;
  } catch (error) {
    console.error("Error fetching settings:", error);
    throw error;
  }
};

// Update settings in the backend
export const updateSettings = async (settings) => {
  try {
    const response = await apiService.post("/api/settings", settings);
    return response.data;
  } catch (error) {
    console.error("Error updating settings:", error);
    throw error;
  }
};

/**
 * Fetch internal vending machines (own machines)
 * @param {number} page - The current page number (default is 0)
 * @param {number} size - The number of items per page (default is 10)
 * @param {string} sortBy - The field to sort by (default is 'location')
 * @param {string} direction - The sorting direction (default is 'ASC')
 * @returns {Promise} - Internal vending machines data
 */

export const getOwnMachines = async (
  sortBy = "location",
  location = "",
  status = "",
  condition = "",
  size = 10,
  page = 0
) => {
  try {
    const params = {
      sortBy,
      location,
      size,
      page,
    };

    if (status === "Active" || status === "Inactive") {
      params.status = status;
    }

    if (status === "Normal" || status === "Abnormal") {
      params.condition = status;
    }

    const response = await apiService.get(
      "/api/vending-machines/own-machines",
      { params }
    );

    return response.data;
  } catch (error) {
    console.error("Error fetching own machines:", error);
    throw error;
  }
};


/**
 * Fetch franchise vending machines
 * @param {string} sortBy - The field to sort by (default is 'location')
 * @returns {Promise} - Franchise vending machines data
 */
export const getFranchiseMachines = async (
  sortBy = "location",
  location = "",
  status = "",
  condition = "",
) => {
  try {
    // Dynamically construct the params object
    const params = { sortBy };
    if (location !== "") params.location = location;
    if (status === "Active" || status === "Inactive") params.status = status;
    if (status === "Normal" || status === "Abnormal") params.condition = status;
    const response = await apiService.get(
      "/api/vending-machines/all-franchise-machines",
      {
        params,
      },
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching franchise machines:", error);
    throw error;
  }
};

export const addVendingMachine = async (formData) => {
  try {
    const response = await apiService.post(
      "/api/vending-machines/add",
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      },
    );
    return response.data;
  } catch (error) {
    console.error("Error adding vending machine:", error);
    throw error;
  }
};

export const updateVendingMachine = async (id, formData) => {
  try {
    const response = await apiService.put(
      `/api/vending-machines/${id}/update`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      },
    );
    return response.data;
  } catch (error) {
    console.error("Error updating vending machine:", error);
    throw error;
  }
};

/**
 * Fetch details of a specific vending machine by its ID
 * @param {number|string} id - The ID of the vending machine
 * @returns {Promise} - Vending machine details
 */
export const getMachineDetails = async (id) => {
  try {
    const response = await apiService.get(`/api/vending-machines/${id}`);
    return response.data;
  } catch (error) {
    console.error(
      `Error fetching vending machine details with ID ${id}:`,
      error,
    );
    throw error;
  }
};

export const getFranchiseMachineDetails = async (id) => {
  try {
    const response = await apiService.get(
      `/api/vending-machines/franchise/${id}`,
    );
    return response.data;
  } catch (error) {
    console.error(
      `Error fetching vending machine details with ID ${id}:`,
      error,
    );
    throw error;
  }
};

export const updateMachineSettings = async (id, settings) => {
  try {
    const response = await apiService.patch(
      `/api/vending-machines/${id}/temperature`,
      settings,
    );
    return response.data;
  } catch (error) {
    console.error("Error updating machine settings:", error);
    throw error;
  }
};

export const exportSlotsData = async (machineId) => {
  try {
    const response = await apiService.get(
      `api/aisle-slots/${machineId}/export`,
      {
        responseType: "blob", // Important for downloading files
      },
    );
    return response.data;
  } catch (error) {
    console.error("Error exporting slots data:", error);
    throw error;
  }
};

export const getAisleData = async (machineNumber, tags = []) => {
  try {
    // Construct the query string if tags are provided
    const queryString = tags.length ? `?tags=${tags.join(",")}` : "";
    const response = await apiService.get(
      `/api/aisle-slots/${machineNumber}/all${queryString}`,
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching aisle data:", error);
    throw error;
  }
};

/**
 * Updates the slot with product details.
 * @param {number} slotId - The ID of the slot to update.
 * @param {Object} data - The data to update the slot with.
 * @param {number} data.productId - The ID of the product to add.
 * @param {number} data.quantity - The quantity of the product in the slot.
 * @param {number} data.capacity - The total capacity of the slot.
 * @returns {Promise} - A promise that resolves with the API response.
 */
export const updateSlot = async (slotId, data) => {
  try {
    const response = await apiService.post(
      `/api/aisle-slots/add/${slotId}`,
      data,
      {
        headers: {
          "Content-Type": "application/json",
        },
      },
    );
    return response.data;
  } catch (error) {
    console.error("Error updating slot:", error);
    throw error.response?.data || error.message;
  }
};

export const updateBulkSlot = async (data) => {
  try {
    const response = await apiService.post(
      `/api/aisle-slots/bulk-update`,
      data,
      {
        headers: {
          "Content-Type": "application/json",
        },
      },
    );
    return response.data;
  } catch (error) {
    console.error("Error updating slots:", error);
    throw error.response?.data || error.message;
  }
};

export const emptySlots = async (data) => {
  try {
    const response = await apiService.post(`/api/aisle-slots/empty/all`, data, {
      headers: {
        "Content-Type": "application/json",
      },
    });
    return response.data;
  } catch (error) {
    console.error("Error empty slots:", error);
    throw error.response?.data || error.message;
  }
};

export const getTempRecords = async (
  machineId,
  page,
  size,
  startDate,
  endDate,
) => {
  try {
    const params = { page, size };
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;
    const response = await apiService.get(
      `/api/vending-machines/machine/${machineId}`,
      { params },
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching temp readings:", error);
    throw error;
  }
};

// Fetch product based on the category id
export const getProducts = async (
  page = 0,
  size = 10,
  categoryId,
  search = "",
  sort = "",
  filter = "",
) => {
  try {
    const params = { page, size, search };
    if (sort === "ascending" || sort === "descending") params.priceOrder = sort;
    if (filter !== "") params.tag = filter;
    const response = await apiService.get(
      `/api/inventory/products/category/${categoryId}`,
      {
        params,
      },
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching peoducts:", error);
    throw error;
  }
};

// adding the product
export const addProduct = async (formData) => {
  try {
    const response = await apiService.post(
      "/api/inventory/products/add",
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      },
    );
    return response.data;
  } catch (error) {
    console.error("Error adding peoduct:", error);
    throw error;
  }
};

// update the product
export const updateProduct = async (id, formData) => {
  try {
    const response = await apiService.put(
      `/api/inventory/products/${id}`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      },
    );
    return response.data;
  } catch (error) {
    console.error("Error updating the product:", error);
    throw error;
  }
};

// delete the product
export const deleteProduct = async (id) => {
  try {
    const response = await apiService.delete(`/api/inventory/products/${id}`);
    return response.data;
  } catch (error) {
    console.error("Error deleting the product:", error);
    throw error;
  }
};

// Fetch coupons
export const getCoupons = async (
  page = 0,
  size = 10,
  search = "",
  status = "",
) => {
  try {
    const params = { page, size, search };
    if (status === "Active" || status === "Inactive" || status === "Expired")
      params.status = status;
    if (status === "Normal" || status === "Abnormal") params.condition = status;
    const response = await apiService.get("/api/coupons", {
      params,
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching coupons:", error);
    throw error;
  }
};

// export coupons
export const exportCoupons = async (couponId, isUsed) => {
  try {
    const response = await apiService.get(`/api/coupons/export/${couponId}`, {
      responseType: "blob", // Handles the binary file correctly
      params: { isUsed: isUsed },
    });
    return response.data; // Return the Blob data
  } catch (error) {
    console.error(
      "Error exporting coupons:",
      error.response?.data || error.message,
    );
    throw error; // Re-throw the error to be handled by the calling function
  }
};

// export coupons
export const previewCouponss = async (couponId) => {
  try {
    const response = await apiService.get(`/api/coupons/${couponId}/preview`);
    return response.data; // Return the Blob data
  } catch (error) {
    console.error(
      "Error previewing coupons:",
      error.response?.data || error.message,
    );
    throw error; // Re-throw the error to be handled by the calling function
  }
};

// generate coupons
export const generateCoupons = async (formData) => {
  try {
    const response = await apiService.post("/api/coupons", formData);

    return response.data;
  } catch (error) {
    console.error("Error generating coupons:", error);
    throw error;
  }
};

// location wise machines
export const getMachineLocations = async (machineType) => {
  try {
    const response = await apiService.get(
      "/api/vending-machines/location-wise-machines",
      {
        params: machineType ? { machineType } : {},
      }
    );

    return response.data;
  } catch (error) {
    console.error("Error fetching machine locations:", error);
    throw error;
  }
};

export const deleteCoupon = async (id) => {
  try {
    const response = await apiService.delete(`/api/coupons/${id}`);
    return response.data;
  } catch (error) {
    console.error(`Error deleting coupon with ID ${id}:`, error);
    throw error;
  }
};

export const updateGeneratedCoupons = async (id, couponData) => {
  try {
    const response = await apiService.put(`/api/coupons/${id}`, couponData);
    return response.data;
  } catch (error) {
    console.error("Error updating coupons:", error);
    throw error;
  }
};

// Fetch users
export const getUsers = async (page = 0, size = 10, search = "") => {
  try {
    const response = await apiService.get("/api/admin/users", {
      params: { page, size, search },
    });
    return response.data.data;
  } catch (error) {
    console.error("Error fetching users:", error);
    throw error;
  }
};

export const addUser = async (formData) => {
  try {
    const response = await apiService.post("/api/admin/users", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
    return response.data;
  } catch (error) {
    console.error("Error adding user:", error);
    throw error;
  }
};

export const updateUser = async (formData) => {
  try {
    const response = await apiService.put("/api/admin/users", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
    return response.data;
  } catch (error) {
    console.error("Error updating user:", error);
    throw error;
  }
};

export const deleteUser = async (login) => {
  try {
    const response = await apiService.delete(`/api/admin/users/${login}`);
    return response.data;
  } catch (error) {
    console.error("Error deleting user:", error);
    throw error;
  }
};

// post the discount
export const createDiscount = async (data) => {
  try {
    const response = await apiService.post("/api/discounts/add", data);
    return response.data.data;
  } catch (error) {
    console.error("Error creating discount:", error);
    throw error;
  }
};

/**
 * Fetch discounts with pagination, search, and filter
 * @param {number} page - The current page number (default is 0)
 * @param {number} size - The number of items per page (default is 10)
 * @param {string} search - The search term for filtering by company name
 * @param {string} status - The status filter (e.g., 'Active', 'Inactive', 'Expired')
 * @returns {Promise} - Discounts data
 */
export const getDiscounts = async (
  page = 0,
  size = 10,
  search = "",
  status = "",
) => {
  try {
    const params = { page, size, search };
    if (status === "Active" || status === "Inactive" || status === "Expired")
      params.status = status;
    const response = await apiService.get("/api/discounts", {
      params,
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching discounts:", error);
    throw error;
  }
};

/**
 * Delete a discount by ID
 * @param {number} id - The ID of the discount to delete
 * @returns {Promise} - Response from the API
 */
export const deleteDiscount = async (id) => {
  try {
    const response = await apiService.delete(`/api/discounts/${id}`);
    return response.data;
  } catch (error) {
    console.error(`Error deleting discount with ID ${id}:`, error);
    throw error;
  }
};

export const updateDiscount = async (id, cardData) => {
  try {
    const response = await apiService.patch(`/api/discounts/${id}`, cardData);
    return response.data.data;
  } catch (error) {
    console.error("Error updating discount:", error);
    throw error;
  }
};

export const getFranchises = async () => {
  try {
    const response = await apiService.get("/api/franchises/all");

    // console.log("FRANCHISE FULL AXIOS RESPONSE:", response);
    // console.log("FRANCHISE RESPONSE DATA:", response?.data);
    return response.data;
  } catch (error) {
    console.error("Error fetching franchises:", error);
    throw error;
  }
};

export const getFranchise = async (id) => {
  try {
    const response = await apiService.get(`/api/franchises/${id}`);
    return response.data;
  } catch (error) {
    console.error("Error getting franchise:", error);
    throw error;
  }
};

export const getSingleFranchise = async () => {
  try {
    const response = await apiService.get(`/api/franchises/my-franchise`);
    return response.data;
  } catch (error) {
    console.error("Error getting franchise:", error);
    throw error;
  }
};

export const addFranchise = async (formData) => {
  try {
    const response = await apiService.post("/api/franchises", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
    return response.data;
  } catch (error) {
    console.error("Error adding franchise:", error);
    throw error;
  }
};

export const updateFranchise = async (id, formData) => {
  try {
    const response = await apiService.put(`/api/franchises/${id}`, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
    return response.data;
  } catch (error) {
    console.error("Error updating franchise:", error);
    throw error;
  }
};

export const updateFranchiseBasicDetails = async (id, formData) => {
  try {
    const response = await apiService.put(
      `/api/franchises/update/${id}`,
      formData,
    );
    return response.data;
  } catch (error) {
    console.error("Error updating franchise:", error);
    throw error;
  }
};

export const deleteFranchise = async (id) => {
  try {
    const response = await apiService.delete(`/api/franchises/${id}`);
    return response.data;
  } catch (error) {
    console.error("Error deleting franchise:", error);
    throw error;
  }
};

// post category
export const addCategory = async (data) => {
  try {
    const response = await apiService.post("/api/categories/add", data);
    return response.data;
  } catch (error) {
    console.error("Error adding category:", error);
    throw error;
  }
};

// Update category name
export const updateCategoryName = async (categoryId, newName) => {
  try {
    const response = await apiService.put(
      `/api/categories/update-name?categoryId=${categoryId}`,
      {
        name: newName,
      },
    );
    return response.data;
  } catch (error) {
    console.error("Error updating category name:", error);
    throw error;
  }
};

// delete category name
export const deleteCategoryName = async (categoryId) => {
  try {
    const response = await apiService.delete(
      `/api/categories/delete?categoryId=${categoryId}`,
    );
    return response.data;
  } catch (error) {
    console.error("Error deleting category name:", error);
    throw error;
  }
};

// Fetch category list with product count
export const getCategoryList = async () => {
  try {
    const response = await apiService.get("/api/categories");
    return response.data;
  } catch (error) {
    console.error("Error fetching category list:", error);
    throw error;
  }
};

// Fetch advertisements
export const getLiveAdvertisements = async () => {
  try {
    const response = await apiService.get("/api/advertisement/live");
    return response.data;
  } catch (error) {
    console.error("Error fetching advertisements:", error);
    throw error;
  }
};

export const getAdvertisements = async () => {
  try {
    const response = await apiService.get("/api/advertisement/non-live");
    return response.data;
  } catch (error) {
    console.error("Error fetching advertisements:", error);
    throw error;
  }
};

export const getAdvertisementsById = async (id) => {
  try {
    const response = await apiService.get(`/api/advertisement/${id}`);
    return response.data;
  } catch (error) {
    console.error(`Error getting advertisment with ID ${id}:`, error);
    throw error;
  }
};

export const addAdvertisement = async (formData) => {
  try {
    const response = await apiService.post("/api/advertisement", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
    return response.data.data;
  } catch (error) {
    console.error("Error adding advertisement:", error);
    throw error;
  }
};

export const updateAdvertisement = async (id, formData) => {
  try {
    const response = await apiService.put(
      `/api/advertisement/${id}`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      },
    );
    return response.data;
  } catch (error) {
    console.error("Error updating advertisement:", error);
    throw error;
  }
};
export const deleteAdvertisement = async (id) => {
  try {
    const response = await apiService.delete(`/api/advertisement/${id}`);
    return response.data.data;
  } catch (error) {
    console.error("Error deleting the advertisement:", error);
    throw error;
  }
};

export const convertAdvertisementVideo = async (file) => {
  try {
    const formData = new FormData();
    formData.append("video", file);

    const response = await apiService.post(
      "/api/advertisement/video/convert",
      formData,
      {
        headers: { "Content-Type": "multipart/form-data" },
        responseType: "blob",
      },
    );
    return response.data; // Blob
  } catch (error) {
    console.error("Error converting advertisement video:", error);
    throw error;
  }
};

// Home Page Apis

export const getMachineBasicDetails = async () => {
  try {
    const response = await apiService.get("/api/vending-machines/franchise");
    return response.data;
  } catch (error) {
    console.error("Error fetching machine details:", error);
    throw error;
  }
};

export const getMapData = async () => {
  try {
    const response = await apiService.get(
      "/api/vending-machines/franchise/map",
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching map details:", error);
    throw error;
  }
};

export const getTodaySales = async () => {
  try {
    const response = await apiService.get("/api/transactions/todays-summary");
    return response.data;
  } catch (error) {
    console.error("Error fetching today sales:", error);
    throw error;
  }
};

export const getSalesCardsCount = async () => {
  try {
    const response = await apiService.get(
      "/api/transactions/sales-summary/totals",
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching sales count:", error);
    throw error;
  }
};

export const getTodaySalesSummary = async (type) => {
  try {
    const response = await apiService.get("/api/transactions/sales-summary", {
      params: { type },
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching sales summary:", error);
    throw error;
  }
};

export const getSalesByLocation = async (startDate = "", endDate = "") => {
  console.log("startDate =", startDate);
  console.log("endDate =", endDate);

  const params = {
    startDate,
    endDate,
  };

  // console.log("params =", params);

  const response = await apiService.get("/api/transactions/salesBylocation", {
    params,
  });

  return response.data;
};

export const getSalesByPaymentMethods = async (startDate, endDate, vmIds) => {
  try {
    // Build params conditionally
    const params = {};
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;
    if (vmIds) params.vendingMachineIds = vmIds;

    const response = await apiService.get(
      "/api/transactions/salesByPaymentTypes",
      { params },
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching sales by payment methods:", error);
    throw error;
  }
};

export const getSalesByDiscountCoupons = async (startDate, endDate, vmIds) => {
  try {
    // Build params conditionally
    const params = {};
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;
    if (vmIds) params.vendingMachineIds = vmIds;

    const response = await apiService.get(
      "/api/transactions/salesByCouponsDiscount",
      { params },
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching sales by coupons and discounts:", error);
    throw error;
  }
};

export const getSalesByProducts = async (startDate, endDate, vmIds) => {
  try {
    // Build params conditionally
    const params = {};
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;
    if (vmIds) params.vendingMachineIds = vmIds;

    const response = await apiService.get("/api/transactions/salesByProducts", {
      params,
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching sales by products:", error);
    throw error;
  }
};

// Transactions
export const getTransactionsData = async (params) => {
  try {
    const response = await apiService.get("/api/transactions", {
      params: {
        page: params.page || 0, // Default page to 0
        size: params.size || 10, // Default size to 10
        sortBy: params.sortBy || "transactionDate", // Default sort field
        direction: params.direction || "DESC", // Default direction to ascending
        machineIds: params.machineIds || [], // Default locations to an empty array
        from: params.from, // Required - must be provided
        to: params.to, // Required - must be provided
        status: params.status,
      },
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching transactions details:", error);
    throw error;
  }
};

export const getReturnTransactionsData = async (params) => {
  try {
    const response = await apiService.get("/api/transactions/return", {
      params,
    });

    // console.log("GET RETURN TRANSACTIONS RESPONSE:", response.data);

    return response.data;
  } catch (error) {
    console.error("Error fetching return transactions:", error);
    throw error;
  }
};

// Taxes
export const getRecentTaxReport = async () => {
  try {
    const response = await apiService.get("/api/taxes/recent");
    return response.data;
  } catch (error) {
    console.error("Error fetching tax report:", error);
    throw error;
  }
};

export const getTaxReport = async (page = 0, size = 10, search = "") => {
  try {
    const response = await apiService.get("/api/taxes/remaining", {
      params: { page, size, search },
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching tax report:", error);
    throw error;
  }
};

export const getTaxReportPDF = async (
  franchiseId,
  month,
  fileName = "Monthly_Transaction_Report"
) => {
  try {
    const response = await apiService.get("/api/taxes/download-pdf", {
      params: { franchiseId, month },
      responseType: "blob",
    });

    const blob = new Blob([response.data], {
      type: "application/pdf",
    });

    const url = window.URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.href = url;
    a.download = `${fileName}_${month}.pdf`;
    a.click();

    window.URL.revokeObjectURL(url);
  } catch (error) {
    console.error("Download failed:", error);
  }
};

export const getMonthlyTaxReport = async (month, startDate, endDate, vmIds) => {
  try {
    // Build params conditionally
    const params = { month };
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;
    if (vmIds) params.vendingMachineIds = vmIds;
    const response = await apiService.get("/api/taxes/report/monthly", {
      params,
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching tax report:", error);
    throw error;
  }
};

export const getDailyTaxReport = async (
  page = 0,
  size = 10,
  month,
  startDate,
  endDate,
  vmIds,
) => {
  try {
    // Build params conditionally
    const params = { page, size, month };
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;
    if (vmIds) params.vendingMachineIds = vmIds;

    const response = await apiService.get("/api/taxes/daily", { params });
    return response.data;
  } catch (error) {
    console.error("Error fetching daily tax report:", error);
    throw error;
  }
};

export const getTaxDetails = async () => {
  try {
    const response = await apiService.get("/api/taxes/taxDetails");
    return response.data;
  } catch (error) {
    console.error("Error fetching tax details:", error);
    throw error;
  }
};

export const addTaxDetails = async (formData) => {
  try {
    const response = await apiService.post("/api/taxes", formData);
    return response.data;
  } catch (error) {
    console.error("Error adding tax details:", error);
    throw error;
  }
};

// service request
export const getServiceRequests = async (
  page = 0,
  size = 10,
  search = "",
  status = "",
) => {
  try {
    const params = { page, size, location: search };
    if (status) params.status = status;

    const response = await apiService.get("/api/service-requests", {
      params,
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching service request details:", error);
    throw error;
  }
};

export const getServiceRequest = async (requestId) => {
  try {
    const response = await apiService.get(`/api/service-requests/${requestId}`);
    return response.data;
  } catch (error) {
    console.error("Error fetching service request details:", error);
    throw error;
  }
};

export const getServiceRequestVisits = async (requestId) => {
  try {
    const response = await apiService.get(
      `/api/service-requests/${requestId}/visits`,
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching service request details:", error);
    throw error;
  }
};

// Fetch product based on the category id
export const getServiceRequestCardsCount = async () => {
  try {
    const response = await apiService.get(
      "/api/service-requests/status-counts",
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching service-requests cards count:", error);
    throw error;
  }
};

export const addServiceRequest = async (data) => {
  try {
    const response = await apiService.post(`/api/service-requests`, data, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
    return response.data;
  } catch (error) {
    console.error("Error creating service request:", error);
    throw error;
  }
};

export const addServiceRequestVisit = async (data) => {
  try {
    const response = await apiService.put(`/api/service-requests/update`, data);
    return response.data;
  } catch (error) {
    console.error("Error adding visit data:", error);
    throw error;
  }
};

export const updateServiceRequest = async (id, data) => {
  try {
    const response = await apiService.patch(
      `/api/service-requests/${id}`,
      data,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      },
    );
    return response.data;
  } catch (error) {
    console.error("Error updating service request:", error);
    throw error;
  }
};

export const updateServiceRequestStatus = async (id, data) => {
  try {
    const response = await apiService.put(
      `/api/service-requests/${id}/status`,
      data,
    );
    return response.data;
  } catch (error) {
    console.error("Error updating service request status:", error);
    throw error;
  }
};

export const deleteServiceRequest = async (id) => {
  try {
    const response = await apiService.delete(`/api/service-requests/${id}`);
    return response.data;
  } catch (error) {
    console.error(`Error deleting service requests with ID ${id}:`, error);
    throw error;
  }
};

export const changePassword = async (password, newPassword) => {
  try {
    const response = await apiService.post(`/api/account/change-password`, {
      currentPassword: password,
      newPassword: newPassword,
    });
    return response.data;
  } catch (error) {
    console.error(`Error updating password:`, error);
    throw error;
  }
};

// Spare Parts
export const getSpareParts = async (category, search = "", sort = "") => {
  try {
    const response = await apiService.get("/api/spare-parts/category", {
      params: { category, search, sort },
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching spare parts:", error);
    throw error;
  }
};

export const addSparePart = async (data) => {
  try {
    const response = await apiService.post(`/api/spare-parts/create`, data, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
    return response.data;
  } catch (error) {
    console.error("Error creating spare spart:", error);
    throw error;
  }
};

export const updateSparePart = async (id, data) => {
  try {
    const response = await apiService.put(
      `/api/spare-parts/update/${id}`,
      data,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      },
    );
    return response.data;
  } catch (error) {
    console.error("Error updating spare part:", error);
    throw error;
  }
};

export const deleteSparePart = async (id) => {
  try {
    const response = await apiService.delete(`/api/spare-parts/delete/${id}`);
    return response.data;
  } catch (error) {
    console.error(`Error deleting spare parts with ID ${id}:`, error);
    throw error;
  }
};

export const getCartData = async () => {
  try {
    const response = await apiService.get("/api/cart/my-cart");
    return response.data;
  } catch (error) {
    console.error("Error fetching cart data:", error);
    throw error;
  }
};

export const addSparePartInCart = async (data) => {
  try {
    const response = await apiService.post(`/api/cart/add`, data);
    return response.data;
  } catch (error) {
    console.error("Error adding parts in cart:", error);
    throw error;
  }
};

export const updateSparePartCart = async (itemId, quantity) => {
  try {
    const response = await apiService.put(
      `/api/cart/update-quantity/${itemId}`,
      {},
      { params: { quantity } },
    );
    return response.data;
  } catch (error) {
    console.error("Error updating cart quantity:", error);
    throw error;
  }
};

export const deleteSparePartInCart = async (itemId) => {
  try {
    const response = await apiService.delete(`/api/cart/remove/${itemId}`);
    return response.data;
  } catch (error) {
    console.error(
      `Error deleting spare parts in cart with ID ${itemId}:`,
      error,
    );
    throw error;
  }
};

export const createCartPageLink = async ({ successUrl, cancelUrl }) => {
  try {
    const res = await apiService.post(
      `/api/cart/checkout`,
      {},
      { params: { successUrl, cancelUrl, shippingAmount: 25 } },
    );
    return res.data;
  } catch (err) {
    console.error("Error creating payment link:", err);
    throw err;
  }
};

export const getDeliveryAddress = async () => {
  try {
    const response = await apiService.get("/api/cart/delivery");
    return response.data;
  } catch (error) {
    console.error("Error fetching delivery address:", error);
    throw error;
  }
};

export const getBillingAddress = async () => {
  try {
    const response = await apiService.get("/api/cart/billing");
    return response.data;
  } catch (error) {
    console.error("Error fetching billing address:", error);
    throw error;
  }
};

export const addDeliveryAddress = async (data) => {
  try {
    const response = await apiService.post(`/api/cart/delivery`, data);
    return response.data;
  } catch (error) {
    console.error("Error adding delivery address:", error);
    throw error;
  }
};

export const addBillingAddress = async (data) => {
  try {
    const response = await apiService.post(`/api/cart/billing`, data);
    return response.data;
  } catch (error) {
    console.error("Error adding billing address:", error);
    throw error;
  }
};

export const addBillingSameAsDelvery = async (data) => {
  try {
    const response = await apiService.post(
      `/api/cart/billing/same-as-delivery`,
      {},
    );
    return response.data;
  } catch (error) {
    console.error(
      "Error adding billing address same as billing address:",
      error,
    );
    throw error;
  }
};

export const getOrders = async () => {
  try {
    const response = await apiService.get("/api/cart/orders");
    return response.data;
  } catch (error) {
    console.error("Error fetching orders:", error);
    throw error;
  }
};

export const getOrderById = async (orderId) => {
  try {
    const response = await apiService.get(`/api/cart/orders/${orderId}`);
    return response.data;
  } catch (error) {
    console.error("Error fetching order:", error);
    throw error;
  }
};

// export const getPayouts = async (params = {}) => {
//   try {
//     const response = await apiService.get("/api/franchises/payouts", {
//       params,
//     });

//     return response.data;
//   } catch (error) {
//     console.error("Error fetching payouts:", error);
//     throw error;
//   }
// };

export const getFranchiseMachineType = async () => {
  try {
    const response = await apiService.get(
      "/api/vending-machines/franchise/machine-type",
    );

    return response.data;
  } catch (error) {
    console.error("Error fetching franchise machine type:", error);
    throw error;
  }
};
export const updateProfile = async (formData) => {
  try {
    const response = await apiService.put("/api/admin/profile", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
    return response.data;
  } catch (error) {
    console.error("Error updating profile:", error);
    throw error;
  }
};
export const getReturnsDashboard = async (params) => {
  try {
    const response = await apiService.get(
      "/api/transactions/return/dashboard-summary",
      { params },
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching returns dashboard:", error);
    throw error;
  }
};
export const getReturnsByLocation = async (params) => {
  try {
    const response = await apiService.get(
      "/api/transactions/return/by-location",
      { params },
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching returns by location:", error);
    throw error;
  }
};

export const getCashbackByMethod = async (startDate, endDate, machineId) => {
  try {
    const response = await apiService.get(
      "/api/transactions/analysis/cashback-methods",
      {
        params: {
          fromDate: startDate,
          toDate: endDate,
          machineId: machineId?.length ? machineId.join(",") : null,
        },
      },
    );

    return response.data;
  } catch (error) {
    console.error("Error fetching cashback methods:", error);
    throw error;
  }
};
export const getCashbackOverview = async (startDate, endDate, machineId) => {
  try {
    const response = await apiService.get(
      "/api/transactions/analysis/cashback-overview",
      {
        params: {
          fromDate: startDate,
          toDate: endDate,
          machineId: machineId?.length ? machineId.join(",") : null,
        },
      },
    );

    return response.data;
  } catch (error) {
    console.error("Error fetching cashback overview:", error);
    throw error;
  }
};
export const bulkDeleteProducts = async (productIds) => {
  // ponytail: passes array as body via Axios `data` option
  try {
    const response = await apiService.delete("/api/inventory/products/bulk", {
      data: productIds,
    });
    return response.data;
  } catch (error) {
    console.error("Error bulk deleting products:", error);
    throw error;
  }
};
export const getBowlsData = async (membershipId) => {
  try {
    const response = await apiService.get(
      "/api/membership-cards/member/bowls",
      {
        params: { membershipId },
      },
    );
    return response.data.data;
  } catch (error) {
    console.error("Error fetching bowls data:", error);
    throw error;
  }
};
export const getReturnTrack = async (params) => {
  try {
    const response = await apiService.get(
      "/api/transactions/return/bowl-tracking",
      { params },
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching return track:", error);
    throw error;
  }
};
export const getReturnCards = async (params) => {
  try {
    const response = await apiService.get(
      "/api/transactions/return/dashboard",
      { params },
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching return cards:", error);
    throw error;
  }
};
